import {Injectable} from '@angular/core';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/ja';
import {AbstractControl, FormControl} from '@angular/forms';
import loadImage from 'blueimp-load-image';
import {LoadImageResult} from 'blueimp-load-image';

export const TZ_TOKYO = 'Asia/Tokyo';

export interface HMSNumber {
  s: number;
  m: number;
  h: number;
}

export interface ImageFileInfo {
  file: File;
  width: number;
  height: number;
}

export interface ImageSize {
  width: number;
  height: number;
}

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('ja');

@Injectable()
export class UtilService {

  static calcAge(birthDate: string): string {
    const baseDate = new Date();
    const y1 = baseDate.getFullYear().toString().padStart(4, '0');
    const m1 = (baseDate.getMonth() + 1).toString().padStart(2, '0');
    const d1 = baseDate.getDate().toString().padStart(2, '0');

    const y2 = birthDate.substr(0, 4);
    const m2 = birthDate.substr(5, 2);
    const d2 = birthDate.substr(8, 2);
    return String(Math.floor((Number(y1 + m1 + d1) - Number(y2 + m2 + d2)) / 10000));
  }

  /**
   *
   * @param val 値
   * @param digit 小数点の桁数
   */
  static round(val: number, digit: number): number {
    if (!digit) {
      return Math.round(val);
    }
    let tmp = 1;
    for (let i = 0; i < digit; i++) {
      tmp = tmp * 10;
    }
    return Math.round(val * tmp) / tmp;
  }

  static getHMS(second?: number, fullDigit?: boolean): string {
    if (second === undefined) {
      return '';
    }
    const {s, m, h} = UtilService.getHMSNumber(second);

    if (fullDigit) {
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }
    if (h === 0) {
      if (m === 0) {
        return `${String(s)}`;
      }
      return `${String(m)}:${String(s).padStart(2, '0')}`;
    }
    return `${String(h)}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }

  static getJapaneseHMS(second: number): string {
    const {s, m, h} = UtilService.getHMSNumber(second);
    if (h === 0) {
      if (m === 0) {
        return `${String(s)}秒`;
      }
      return `${String(m)}分${String(s).padStart(2, '0')}秒`;
    }
    return `${String(h)}時間${String(m).padStart(2, '0')}分${String(s).padStart(2, '0')}秒`;
  }

  static getHMSNumber(second: number): HMSNumber {
    const s = Math.round(second % 60);
    const m = Math.floor(second / 60 % 60);
    const h = Math.floor(second / 60 / 60);
    return {s, m, h};
  }

  static getYMDd(ms: number): string {
    return dayjs(ms).format('YYYY/M/D (dd)');
  }

  static getYMDHM(ms: number): string {
    return dayjs(ms).format('YYYY/MM/DD HH:mm');
  }

  static getYMDHMS(ms: number): string {
    return dayjs(ms).format('YYYY/MM/DD HH:mm:ss');
  }

  static getCheckString(val: boolean): string {
    return val ? '✔️' : '';
  }

  static deepCopy(obj: any): any {
    return JSON.parse(JSON.stringify(obj));
  }

  static startOfEpochTimeOfJSTMonth = (epochTimeMsec: number): number => {
    return dayjs(epochTimeMsec).tz(TZ_TOKYO).startOf('month').toDate().getTime();
  }

  static endOfEpochTimeOfAfterXDays = (xDays: number): Date => {
    return dayjs().tz(TZ_TOKYO).endOf('day').add(xDays, 'day').toDate();
  }


  static getErrMessage(errMessageList: string[]): string {
    return errMessageList.map(errMessage => `・${errMessage}`).join('\n');
  }

  // imgタグに指定したsrcが404だった場合のデフォルト画像表示用
  static setDefaultImage(event: any): void {
    event.target.src = '/assets/img-default.png';

    // デフォルト画像も404だった場合にonerrorイベントが無限ループするのでonerrorイベントを削除
    event.target.removeAttribute('onerror');
  }

  static async convertImageFilePathToBlob(path: string): Promise<Blob> {
    return new Promise((resolve) => {
      const request = new XMLHttpRequest();
      request.open('GET', path, true);
      request.responseType = 'blob';
      request.onload = async () => {
        resolve(request.response);
      }
      request.send();
    });
  }

  static async convertBlobToFile(blob: Blob): Promise<File> {
    return new Promise((resolve) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        // ファイル名はAPI側で適切な名称に変更されてS3に保存されるため、指定している名称に特に意味は無い
        resolve(new File([blob], 'tmp.jpg', {type: 'image/jpeg'}));
      }

      reader.readAsDataURL(blob);
    });
  }

  // フォームのsubmit時に各FormControlでエラーがある場合はエラーメッセージを表示するために使用
  static markAsTouchedAndDirty(formCtl: FormControl | AbstractControl): void {
    formCtl.markAsTouched();
    formCtl.markAsDirty();
  }

  // 一度エラーが出た後の再入力途中に不適切なタイミングでエラーが出るのを防ぐために使用
  static markAsUntouched(formCtl: FormControl | AbstractControl): void {
    formCtl.markAsUntouched();
  }

  // 参考： https://ginpei.hatenablog.com/entry/20070811/1186846446
  static enableLinkUrl(str: string): string {
    const regexp_url = /((h?)(ttps?:\/\/[a-zA-Z0-9.\-_@:/~?%&;=+#',()*!]+))/g; // ']))/;
    const regexp_makeLink = (all: string, url: string, h: string, href: string) => {
      return '<a href="h' + href + '" target="_blank" class="common-text-link">' + url + '</a>';
    };

    return str.replace(regexp_url, regexp_makeLink);
  }

  static async resizeImage(imageFile: File, maxWidth?: number, maxHeight?: number): Promise<ImageFileInfo> {
    const canvas: LoadImageResult = await loadImage(imageFile, {
      maxWidth: maxWidth ?? undefined,
      maxHeight: maxHeight ?? undefined,
      canvas: true,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high',
    });

    const type: string = imageFile.type;
    const canvasElement: HTMLCanvasElement = canvas.image as HTMLCanvasElement;
    const width: number = canvasElement.width;
    const height: number = canvasElement.height;
    const dataUri: string = canvasElement.toDataURL(type);
    const bin: string = atob(dataUri.split(',')[1]);
    const buffer = new Uint8Array(bin.length);

    for (let i = 0; i < bin.length; i++) {
      buffer[i] = bin.charCodeAt(i);
    }

    const blob: Blob = new Blob([buffer], {type: type});

    return {
      file: UtilService.blobToFile(blob),
      width: width,
      height: height,
    }
  }

  static async getImageSize(imageFile: File): Promise<ImageSize> {
    return new Promise((resolve) => {
      const image = new Image();

      image.onload = () => {
        resolve({
          width: image.width,
          height: image.height,
        });
      }

      image.src = URL.createObjectURL(imageFile);
    });
  }

  // 参考： https://stackoverflow.com/questions/27159179/how-to-convert-blob-to-file-in-javascript
  private static blobToFile(blob: Blob, fileName: string = ''): File {
    const fileTmp: any = blob;

    fileTmp.lastModifiedDate = new Date();
    fileTmp.name = fileName;

    return <File>fileTmp;
  }

  constructor() {
  }
}
